import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import Select from '../Select/Select';
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { useMemo, useCallback } from 'react';

interface SelectControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: string): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const SelectControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: SelectControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Select, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Select, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Remove local state - use the value directly to avoid sync issues
    const handleSelectChange = useCallback((event: any) => {
        handleChange(path, event.target.value);
    }, [handleChange, path]);

    // Memoize the Select component props to prevent unnecessary re-renders
    const selectProps = useMemo(() => ({
        variant: 'outlined' as const,
        error: errors,
        value: value || '', // Use value directly, provide fallback
        required: required,
        enums: schema.enum,
        label: uischema.label,
        description: schema.description,
        onChange: handleSelectChange,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated,
        placeholder: uischema?.options?.placeholder,
    }), [
        errors,
        value,
        required,
        schema.enum,
        uischema.label,
        schema.description,
        handleSelectChange,
        enabled,
        visible,
        appValidated,
        uischema?.options?.placeholder
    ]);

    return <Select {...selectProps} />;
};

export default React.memo(withJsonFormsControlProps(SelectControl));
